import React, { useEffect, useMemo, useState } from 'react'
import './dashboard-v2.scss'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { RootState } from '../../store'
import { DashboardV2SpecificInvestmentPresenter } from './specific-investment-v2-presenter'
import { TransactionDetailsModal } from './components/TransactionDetailsModal'
import { dashboardServices, investServices, kycServices, projectServices } from '../../services'
import {
  DashboardInvestmentGraphValues,
  DashboardInvestmentsGraphs,
  DashboardInvestmentTransactions,
  DashboardInvestmentTransactionsRaw,
  DashboardSummary,
  Investment,
  OpportunityDetails,
} from '../../lib/types/propsType_v1'
import { calculateStartDateFromFilter } from '../../lib/utils/helpers'
import { resolveTransactionAmountCents, normalizeTransactionQuantity } from './dashboard-v2.utils'
import { dashboardGraphFilter } from '../../lib/utils/message'
import { showBanner } from '../../global-state/banner-slice'
import { InvestmentSearchParams } from '../../services/types'
import { TransactionSearchParams } from '../../lib/types'

const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const DashboardSpecificInvestmentV2Container = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { opportunityId } = params
  const { authToken, email, name, userId } = useSelector((state: RootState) => state.investor)

  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>({
    totalInvestedAmount: 0,
    totalReturnsTillDate: 0,
    realizedGains: 0,
    unrealizedGains: 0,
    realizedGainsAmount: 0,
    unrealizedGainsAmount: 0,
    totalUnits: 0,
    currentNav: 0,
  })
  const [searchParams, setSearchParams] = useState<TransactionSearchParams>({
    limit: '10',
    offset: '0',
    investor_portfolio_id: 'All',
  })
  const [investmentsGraphData, setInvestmentsGraphData] = useState<DashboardInvestmentsGraphs>({
    investments: [[]],
  })
  const [transactionsData, setTransactionsData] = useState<DashboardInvestmentTransactions[]>([])
  const [transactionToView, setTransactionToView] =
    useState<DashboardInvestmentTransactions | null>(null)
  const [transactionsCurrentPage, setTransactionsCurrentPage] = useState(1)
  const [transactionsPagination, setTransactionsPagination] = useState({
    total: 10,
    limit: 5,
    count: 5,
    offset: 0,
  })
  const [investmentsGraphFilterValue, setInvestmentsGraphFilterValue] = useState(
    dashboardGraphFilter[4], // 1Y
  )
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()

  const [investorPortfolioIds, setInvestorPortfolioIds] = useState<
    { value: string; text: string }[]
  >([{ text: 'All', value: 'All' }])
  const [userInvestmentData, setUserInvestmentData] = useState<Investment | null>(null)

  const handleFilterChange = (value: string) => {
    const selected = dashboardGraphFilter.find((f) => f.value === value)
    if (selected) setInvestmentsGraphFilterValue(selected)
  }

  const getTransactionsData = async () => {
    const response = await dashboardServices.getTransactionsByOpportunityId(
      String(opportunityId),
      {
        ...searchParams,
        investor_portfolio_id:
          searchParams.investor_portfolio_id === 'All' ? '' : searchParams.investor_portfolio_id,
      },
      { Authorization: authToken || '' },
    )
    if (response?.status === 200) {
      const selectedPortfolioId =
        searchParams.investor_portfolio_id !== 'All' ? searchParams.investor_portfolio_id : ''
      const firstKnownPortfolioId =
        investorPortfolioIds.find((item) => item.value && item.value !== 'All')?.value || ''
      const portfolioIdFallback = selectedPortfolioId || firstKnownPortfolioId || '-'

      const transactions =
        response?.transactions?.map((item: DashboardInvestmentTransactionsRaw) => {
          return {
            transaction_date: item.date || '',
            transaction_type: item.txn_type,
            amount: resolveTransactionAmountCents(item),
            quantity: normalizeTransactionQuantity(item.quantity),
            order_status: item.order_status,
            portfolio_id: item.portfolio_id || portfolioIdFallback,
            order_id: item.order_id || '-',
            opportunity_title: '',
            asset_class: '',
            termsheet: item.termsheet ?? null,
            allotmentConfirmation: item.allotmentConfirmation ?? null,
            paymentCopy: item.paymentCopy ?? null,
            actual_amount_received: toNullableNumber(item.actual_amount_received),
            actual_invested_amount: toNullableNumber(item.actual_invested_amount),
            actual_amount_sent: toNullableNumber(item.actual_amount_sent),
            shares: toNullableNumber(item.shares),
            price_per_share: toNullableNumber(item.price_per_share),
          } as DashboardInvestmentTransactions
        }) ?? []
      setTransactionsData(transactions)
      setTransactionsPagination({
        ...transactionsPagination,
        total: response?.total,
        limit: response?.limit ?? transactionsPagination.limit,
        count: response?.count ?? transactionsPagination.count,
        offset: response?.offset ?? transactionsPagination.offset,
      })
    }
  }

  useEffect(() => {
    const getOpportunityById = async () => {
      const data = await projectServices.getOpportunityById(opportunityId as string, {
        Authorization: authToken || '',
      })
      if (data?.status === 200 && data?.data) {
        setOpportunity(data.data)
      }
    }

    const getUserInvestmentInOpportunity = async () => {
      return investServices.searchInvestments_v1(
        {
          investor_id: userId,
          limit: 1,
          offset: 0,
          opportunity_id: opportunityId,
        } as InvestmentSearchParams,
        { Authorization: authToken || '' },
      )
    }

    /** Secondary-market buyers may hold via transaction_history only (no investments row). */
    const hasLedgerExposureForOpportunity = async (): Promise<boolean> => {
      const txnResponse = await dashboardServices.getTransactionsByOpportunityId(
        String(opportunityId),
        { limit: '1', offset: '0', investor_portfolio_id: 'All' },
        { Authorization: authToken || '' },
      )
      if (txnResponse?.status !== 200) return false
      return Number(txnResponse?.total ?? 0) > 0 || (txnResponse?.transactions?.length ?? 0) > 0
    }

    const getDashboardInvestmentSummary = async () => {
      const data = await dashboardServices.getInvestmentSummary(String(opportunityId), {
        Authorization: authToken || '',
      })
      if (data?.status === 200 && data?.data) {
        setDashboardSummary(data.data)
      }
    }

    const getDashboardInvestmentsGraphs = async () => {
      const startDate = calculateStartDateFromFilter(investmentsGraphFilterValue.value)
      const data = await dashboardServices.getInvestmentsGraphsByOpportunityId(
        { startDate },
        String(opportunityId),
        { Authorization: authToken || '' },
      )
      if (data?.status === 200 && data?.data) {
        const response = data.data
        const investments =
          response?.values?.map((item: DashboardInvestmentGraphValues) => ({
            date: item.date,
            currentInvestmentValue: item.currentInvestmentValue,
            opportunity_id: response?.opportunity_id,
            opportunity_title: response?.opportunity_title,
          })) ?? []
        setInvestmentsGraphData({ investments: [investments] })
      }
    }

    const getInvestorPortfolioIds = async () => {
      const response = await kycServices.getInvestorPortfolioIds(userId, {
        Authorization: authToken || '',
      })
      if (response?.data && response?.status === 200) {
        const portfolioIds = response.data?.map((item: { investor_portfolio_id: string }) => ({
          value: item.investor_portfolio_id,
          text: item.investor_portfolio_id,
        }))
        setInvestorPortfolioIds([{ text: 'All', value: 'All' }, ...portfolioIds])
      }
    }

    if (authToken && userId && opportunityId) {
      getUserInvestmentInOpportunity().then(async (response) => {
        if (response?.status !== 200) return

        const hasInvestments =
          Number(response?.count ?? 0) !== 0 && Number(response?.total ?? 0) !== 0

        if (!hasInvestments) {
          const hasLedger = await hasLedgerExposureForOpportunity()
          if (!hasLedger) {
            dispatch(
              showBanner({
                text: 'You do not have any investments in that opportunity',
                variant: 'warning-banner',
              }),
            )
            navigate('/dashboard')
            return
          }
        } else if (response?.investments && response.investments.length > 0) {
          setUserInvestmentData(response.investments[0])
        }

        getOpportunityById()
        getDashboardInvestmentSummary()
        getDashboardInvestmentsGraphs()
        getTransactionsData()
        getInvestorPortfolioIds()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (authToken) getTransactionsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionsCurrentPage, searchParams])

  useEffect(() => {
    if (authToken && userId && opportunityId) {
      const load = async () => {
        const startDate = calculateStartDateFromFilter(investmentsGraphFilterValue.value)
        const data = await dashboardServices.getInvestmentsGraphsByOpportunityId(
          { startDate },
          String(opportunityId),
          { Authorization: authToken || '' },
        )
        if (data?.status === 200 && data?.data) {
          const response = data.data
          const investments =
            response?.values?.map((item: DashboardInvestmentGraphValues) => ({
              date: item.date,
              currentInvestmentValue: item.currentInvestmentValue,
              opportunity_id: response?.opportunity_id,
              opportunity_title: response?.opportunity_title,
            })) ?? []
          setInvestmentsGraphData({ investments: [investments] })
        }
      }
      load()
    }
  }, [investmentsGraphFilterValue.value, authToken, userId, opportunityId])

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = transactionsPagination
    setTransactionsCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setTransactionsPagination({ ...transactionsPagination, offset: (currentPage - 1) * limit })
  }

  const processedTransactionsData = useMemo(() => {
    return (
      transactionsData?.map((item: DashboardInvestmentTransactions) => ({
        ...item,
        asset_class: opportunity?.assetClass ?? '',
        opportunity_title: opportunity?.projectName ?? '',
      })) ?? []
    )
  }, [transactionsData, opportunity])
  const currentNavValue = useMemo(() => {
    const nav = dashboardSummary?.currentNav
    if (typeof nav === 'number' && nav >= 0) return nav
    if (opportunity?.assetClass === 'Trade Finance' && userInvestmentData?.unit_buy_price) {
      return userInvestmentData.unit_buy_price
    }
    if (dashboardSummary?.totalUnits && dashboardSummary.totalUnits > 0) {
      const currentValue =
        Number(dashboardSummary.totalInvestedAmount || 0) +
        Number(dashboardSummary.totalReturnsTillDate || 0)
      return currentValue / dashboardSummary.totalUnits
    }
    return 0
  }, [dashboardSummary, opportunity?.assetClass, userInvestmentData?.unit_buy_price])

  return (
    <>
      <DashboardV2SpecificInvestmentPresenter
        investorName={name || email || 'Client'}
        opportunity={opportunity}
        summary={dashboardSummary}
        currentNavValue={currentNavValue}
        graphFilter={investmentsGraphFilterValue}
        onChangeFilter={handleFilterChange}
        onBackToDashboard={() => navigate('/dashboard')}
        graphSeries={investmentsGraphData}
        transactions={processedTransactionsData}
        transactionsPage={transactionsCurrentPage}
        transactionsPagination={transactionsPagination}
        onChangeTransactionsPage={handleCurrentPage}
        onTransactionClick={(txn) => setTransactionToView(txn)}
      />
      <TransactionDetailsModal
        isOpen={transactionToView !== null}
        transaction={transactionToView}
        onClose={() => setTransactionToView(null)}
      />
    </>
  )
}

export { DashboardSpecificInvestmentV2Container }
